import { useMemo, useState } from 'react';
import DiscussionForm from '@/components/feature/DiscussionForm';
import { topicComments } from '@/mocks/gossip';

interface CommentSectionProps {
  topicId: string;
}

export default function CommentSection({ topicId }: CommentSectionProps) {
  const [sort, setSort] = useState<'hot' | 'new'>('hot');
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const comments = useMemo(() => {
    const list = topicComments.filter((item) => item.topicId === topicId);
    return sort === 'hot' ? [...list].sort((a, b) => b.likes - a.likes) : [...list].sort((a, b) => a.floor - b.floor);
  }, [topicId, sort]);

  const toggleLike = (id: string) => setLiked((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <section id="comments" className="mt-10">
      <div className="flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-center md:justify-between">
        <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
          <i className="ri-chat-smile-2-line text-primary-500" aria-hidden="true" />
          评论区 · {comments.length} 条观点
        </h2>
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {[
            { key: 'hot', label: '最热' },
            { key: 'new', label: '最新' },
          ].map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSort(option.key as 'hot' | 'new')}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                sort === option.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-4">
        {comments.length > 0 ? (
          comments.map((comment) => (
            <article key={comment.id} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-background-50 ${comment.avatarBg}`}
                  aria-hidden="true"
                >
                  {comment.user.slice(0, 1)}
                </span>
                <span className="flex flex-col">
                  <span className="text-sm font-semibold text-foreground-900">{comment.user}</span>
                  <span className="text-[11px] text-foreground-400">
                    {comment.time} · {comment.floor} 楼
                  </span>
                </span>
                <span className="ml-auto rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-500">
                  {comment.replies} 条回复
                </span>
              </div>
              <p className="mt-3.5 text-[13px] leading-relaxed text-foreground-700 md:text-sm">{comment.content}</p>
              <div className="mt-3.5 flex items-center gap-2 border-t border-background-100 pt-3">
                <button
                  type="button"
                  onClick={() => toggleLike(comment.id)}
                  aria-pressed={Boolean(liked[comment.id])}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    liked[comment.id]
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-500 hover:bg-background-100'
                  }`}
                >
                  <i className={liked[comment.id] ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} aria-hidden="true" />
                  {comment.likes + (liked[comment.id] ? 1 : 0)}
                </button>
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-foreground-400">
                  <i className="ri-chat-3-line" aria-hidden="true" />
                  回复
                </span>
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-foreground-400">
                  <i className="ri-share-forward-line" aria-hidden="true" />
                  分享
                </span>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
            <i className="ri-chat-new-line text-3xl text-foreground-300" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-600">这条瓜料还没有人留言，来做第一个吧。</p>
          </div>
        )}
      </div>

      <div className="mt-6">
        <DiscussionForm />
      </div>
    </section>
  );
}